<template>
  <div class="cb-page room-screen">
    <header class="room-screen__header">
      <h1>Игра с другом</h1>
      <p class="room-screen__note">Без звёзд и общего рейтинга</p>
    </header>
    <figure class="room-screen__cover">
      <img v-show="displayMode !== 'MOUTH'" :src="blinkCover" alt="Два котика смотрят друг на друга" width="960" height="417" decoding="async" />
      <img v-show="displayMode === 'MOUTH'" :src="mouthCover" alt="Два котика с открытыми ртами смотрят друг на друга" width="960" height="417" decoding="async" />
      <figcaption>{{ guestToken && !visibleRoom ? 'Друг приглашает вас на дуэль' : caption }}</figcaption>
    </figure>
    <section class="room-screen__mode" aria-label="Режим дуэли">
      <h2>Режим дуэли</h2>
      <ModeSwitch v-if="!guestToken || visibleRoom" :model-value="displayMode" :disabled="!canChangeMode" @update:model-value="changeMode" />
      <p v-else class="room-screen__guest-mode">Режим выбрал хозяин комнаты</p>
      <p class="room-screen__mode-note" aria-live="polite">{{ modeNote }}</p>
    </section>
    <section class="room-screen__camera-card cb-surface" aria-label="Камера">
      <div class="room-screen__preview">
        <video ref="video" autoplay muted playsinline class="room-screen__video" />
        <AppIcon v-if="!cameraReady" name="camera" :size="28" class="room-screen__camera-icon" />
      </div>
      <div class="room-screen__camera-copy">
        <strong><span class="room-screen__dot" :class="{ 'room-screen__dot--ready': cameraReady }"></span>{{ cameraReady ? 'Камера готова' : busy ? 'Проверяем камеру…' : 'Нужен доступ к камере' }}</strong>
        <span>{{ cameraReady ? 'Вы в кадре' : 'Подготовьте камеру перед дуэлью' }}</span>
      </div>
    </section>
    <section class="room-screen__invite cb-surface" aria-label="Приглашение в комнату" :aria-busy="busy">
      <div class="room-screen__invitation-header"><AppIcon name="link" :size="23" /><h2>{{ invitationTitle }}</h2></div>
      <template v-if="inviteUrl && visibleRoom?.state === 'WAITING'">
        <div class="room-screen__link-row">
          <label class="cb-sr-only" for="room-link">Ссылка для друга</label>
          <input id="room-link" class="room-screen__link" :value="inviteUrl" readonly @focus="$event.target.select()" />
          <button class="room-screen__copy-icon" type="button" aria-label="Скопировать ссылку" @click="copy"><AppIcon name="copy" :size="20" /></button>
        </div>
        <p class="room-screen__link-note">Ссылка одна для обоих режимов</p>
        <p class="room-screen__status">Ждём друга · <span class="cb-num">{{ remaining }}</span></p>
      </template>
      <p v-else class="room-screen__status" aria-live="polite">{{ statusText }}</p>
      <div class="room-screen__participants">
        <div class="room-screen__participant">
          <PlayerAvatar :src="session.me?.avatarUrl" :name="session.displayName" :size="36" />
          <div><strong class="cb-ellipsis">{{ session.displayName || 'Вы' }}<span v-if="session.displayName"> · Вы</span></strong><span><span class="room-screen__dot" :class="{ 'room-screen__dot--ready': cameraReady }"></span>{{ cameraReady ? 'Готовы к игре' : 'Готовим камеру' }}</span></div>
        </div>
        <div class="room-screen__participant">
          <div class="room-screen__guest-avatar" aria-hidden="true"><AppIcon name="user" :size="24" /></div>
          <div><strong>{{ visibleRoom?.state === 'PAIRED' ? 'Друг подключился' : 'Друг ещё не подключился' }}</strong><span v-if="visibleRoom?.state === 'PAIRED'"><span class="room-screen__dot room-screen__dot--ready"></span>Готовим дуэль…</span></div>
        </div>
      </div>
      <div class="room-screen__actions">
        <template v-if="inviteUrl && visibleRoom?.state === 'WAITING'">
          <AppButton size="md" icon="send" block @click="share">Отправить ссылку</AppButton>
          <AppButton size="md" icon="copy" variant="secondary" block @click="copy">Скопировать ссылку</AppButton>
        </template>
        <AppButton v-else-if="guestToken && !visibleRoom" size="md" block :loading="busy" @click="enterGuest">Войти в комнату</AppButton>
        <AppButton v-else-if="visibleRoom?.state === 'PAIRED' && !transitioning" size="md" block :loading="busy" @click="startPaired">Продолжить</AppButton>
        <AppButton v-else-if="localError || room.error" size="md" block :loading="busy" @click="openHost">Попробовать снова</AppButton>
        <AppButton v-else-if="visibleRoom?.state === 'CLOSED'" size="md" block :loading="busy" @click="openHost">Новая игра с другом</AppButton>
      </div>
      <p v-if="room.error || localError" class="room-screen__error" role="alert">{{ room.error || localError }}</p>
    </section>
    <button class="room-screen__exit" type="button" :disabled="leaving" @click="exit">{{ leaving ? 'Выходим…' : (!!visibleRoom && visibleRoom.state !== 'CLOSED') || busy ? 'Выйти из комнаты' : 'Главное меню' }}</button>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import AppButton from "@/components/ui/AppButton.vue";
import AppIcon from "@/components/ui/AppIcon.vue";
import ModeSwitch from "@/components/ui/ModeSwitch.vue";
import PlayerAvatar from "@/components/ui/PlayerAvatar.vue";
import { useRoomStore, roomErrorText } from "@/stores/room";
import { useGameStore } from "@/stores/game";
import { useSessionStore } from "@/stores/session";
import { acquireCamera, cameraErrorMessage, releaseCamera } from "@/services/media";
import { getOpponentInfo } from "@/services/api";
import { roomInviteLink, validRoomToken } from "@/services/roomIntent";
import realtime from "@/services/realtime";
import telegram from "@/services/telegram";
import { copyText, formatTimer } from "@/utils/format";
import { useToast } from "@/composables/useToast";
import blinkCover from "@/assets/img/room-cats-blink.webp";
import mouthCover from "@/assets/img/room-cats-mouth.webp";

const room = useRoomStore();
const game = useGameStore();
const session = useSessionStore();
const router = useRouter();
const route = useRoute();
const toast = useToast();
const video = ref(null);
const cameraReady = ref(false);
const busy = ref(false);
const transitioning = ref(false);
const leaving = ref(false);
const localError = ref("");
const clock = ref(Date.now());
const requestedToken = typeof route.query.invite === "string" ? route.query.invite : room.inviteToken;
const guestToken = ref(validRoomToken(requestedToken) ? requestedToken : null);
if (guestToken.value) room.setIntent(guestToken.value);
const visibleRoom = computed(() => guestToken.value ? null : room.snapshot);
const inviteUrl = computed(() => roomInviteLink(visibleRoom.value?.inviteToken, process.env.VUE_APP_BOT_USERNAME, window.location.origin));
const displayMode = computed(() => visibleRoom.value && room.active ? visibleRoom.value.gameMode : session.mode);
const caption = computed(() => displayMode.value === "MOUTH" ? "Кто закроет рот первым?" : "Кто кого пересмотрит?");
const canChangeMode = computed(() => room.snapshot?.state === "WAITING" && !!room.snapshot.inviteToken && !guestToken.value && !busy.value && !room.modePending && !transitioning.value);
const modeNote = computed(() => guestToken.value ? "После входа вы получите актуальный режим" : room.modePending ? "Сохраняем режим…" : room.snapshot?.state === "PAIRED" ? "Друг подключился — режим зафиксирован" : "Можно менять, пока друг не подключился");
const remaining = computed(() => formatTimer(Math.max(0, Math.ceil(((room.snapshot?.expiresAt || 0) - (clock.value + ((room.snapshot?.serverNow || clock.value) - (room.snapshot?.receivedAt || clock.value)))) / 1000))));
const invitationTitle = computed(() => guestToken.value ? "Приглашение от друга" : inviteUrl.value && room.snapshot?.state === "WAITING" ? "Приглашение готово" : room.snapshot?.state === "PAIRED" ? "Друг подключился" : room.snapshot?.state === "CLOSED" ? "Приглашение закрыто" : "Готовим приглашение…");
const statusText = computed(() => guestToken.value ? "Присоединитесь к приватной игре. Режим выбрал хозяин." : room.snapshot?.state === "CLOSED" ? room.snapshot.reason === "EXPIRED" ? "Срок ссылки истёк. Откройте новую игру с другом." : "Комната закрыта. Для новой дуэли нужна новая ссылка." : room.snapshot?.state === "PAIRED" ? "Начинаем дуэль…" : guestToken.value ? "Присоединитесь к приватной игре. Режим выбрал хозяин." : "Создаём комнату с выбранным режимом");
let disposed = false;
let polling = false;
let timer = null;
let openingPromise = null;
let admissionStarted = false;
const off = [];
let cameraPromise = null;
const liveCamera = (stream) => !!stream?.getVideoTracks?.().some((track) => track.readyState === "live");
const stopAcquiredCamera = (stream) => stream?.getTracks?.().forEach((track) => { try { track.stop(); } catch (error) { /* The track may already be stopped. */ } });

async function ensureCamera() {
  if (cameraReady.value && liveCamera(video.value?.srcObject)) return;
  if (cameraPromise) return cameraPromise;
  cameraReady.value = false;
  const task = (async () => {
    try {
      // A disposed previous room can stop a shared pending permission response.
      // Reacquire once, and never announce an ended video track as camera-ready.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const stream = await acquireCamera();
        if (disposed) { stopAcquiredCamera(stream); return; }
        await nextTick();
        if (disposed) { stopAcquiredCamera(stream); return; }
        if (!liveCamera(stream)) continue;
        if (video.value) { video.value.srcObject = stream; await video.value.play().catch(() => null); }
        if (disposed) { stopAcquiredCamera(stream); return; }
        if (!liveCamera(stream)) continue;
        cameraReady.value = true;
        return;
      }
      throw new Error("Не удалось запустить камеру. Попробуйте снова.");
    } catch (error) { throw new Error(cameraErrorMessage(error)); }
  })();
  cameraPromise = task;
  try { await task; } finally { if (cameraPromise === task) cameraPromise = null; }
}

async function startPaired() {
  const created = room.snapshot?.newRound;
  if (disposed || guestToken.value || transitioning.value || room.snapshot?.state !== "PAIRED" || !created?.uuid) return;
  transitioning.value = true;
  busy.value = true;
  try {
    await ensureCamera();
    await realtime.whenOpen(20000);
    if (disposed) return;
    session.setMode(created.gameMode);
    game.startGame({ ...created, privateRoom: true, roomId: room.snapshot.roomId });
    try { game.setOpponent(await getOpponentInfo(created.uuid)); } catch (error) { /* Game session loads the opponent again. */ }
    if (disposed) return;
    game.setPhase("intro");
    guestToken.value = null;
    room.clearIntent();
    await router.replace({ path: "/game", query: { round: created.uuid } });
  } catch (error) { localError.value = roomErrorText(error); transitioning.value = false; }
  finally { busy.value = false; }
}

async function openRoom(token = null) {
  if (busy.value || disposed) return;
  busy.value = true;
  localError.value = "";
  const task = (async () => {
    try {
      await ensureCamera();
      if (disposed) return;
      await realtime.whenOpen(20000);
      if (disposed) return;
      admissionStarted = true;
      if (token) await room.enter(session.mode, token);
      else await room.openHost(session.mode);
      guestToken.value = null;
      if (disposed) return;
      if (room.active) session.setMode(room.snapshot.gameMode);
      if (room.snapshot?.state === "PAIRED") { busy.value = false; await startPaired(); }
    } catch (error) { if (!disposed) localError.value = roomErrorText(error); }
    finally { busy.value = false; admissionStarted = false; }
  })();
  openingPromise = task;
  try { await task; } finally { if (openingPromise === task) openingPromise = null; }
}
function openHost() { return openRoom(); }
function enterGuest() { return openRoom(guestToken.value); }
async function changeMode(mode) {
  if (!canChangeMode.value) return;
  localError.value = "";
  try { await room.changeMode(mode); if (!disposed && room.active) session.setMode(room.snapshot.gameMode); }
  catch (error) { if (!disposed) localError.value = roomErrorText(error); }
  finally { if (!disposed && room.snapshot?.state === "PAIRED") await startPaired(); }
}

async function copy() { if (await copyText(inviteUrl.value)) toast.success("Ссылка скопирована"); else toast.error("Не удалось скопировать ссылку"); }
async function share() { const result = await telegram.shareUrl(inviteUrl.value, "Сыграем в Гляделки? Присоединяйся к моей комнате."); if (result === "copied") toast.success("Ссылка скопирована"); }
async function exit() { if (leaving.value) return; leaving.value = true; await router.replace("/"); }

watch(() => room.snapshot?.state, (state) => { if (state === "PAIRED" && !guestToken.value && !busy.value && !room.modePending) startPaired(); });
async function refresh() {
  clock.value = Date.now();
  if (disposed || guestToken.value || polling || !room.active) return;
  polling = true;
  try { await room.refresh(); } catch (error) { if (!disposed) localError.value = roomErrorText(error); }
  finally { polling = false; }
}

onMounted(async () => {
  off.push(realtime.on("room_state", (value) => { if (!disposed && (!room.snapshot || value.roomId === room.snapshot.roomId)) room.applySnapshot(value); }));
  off.push(realtime.on("reconnected", () => refresh()));
  timer = setInterval(refresh, 1500);
  if (guestToken.value) {
    try { await ensureCamera(); } catch (error) { localError.value = roomErrorText(error); }
    return;
  }
  await openHost();
});

onBeforeRouteLeave(async (to) => {
  if (to.name === "game" && transitioning.value) return true;
  disposed = true;
  // A pending create must finish before leaving, so it cannot leave an orphan room behind.
  if (openingPromise && admissionStarted) await openingPromise.catch(() => null);
  if (!guestToken.value && room.active) {
    try { await room.leave(); } catch (error) { realtime.send({ type: "leave_room", roomId: room.snapshot?.roomId }, { ttl: 2000 }); }
  }
  room.clearIntent();
  guestToken.value = null;
  releaseCamera();
  return true;
});
onBeforeUnmount(() => {
  disposed = true;
  clearInterval(timer);
  off.forEach((remove) => remove());
  if (!transitioning.value) releaseCamera();
});
</script>

<style scoped>
.room-screen { gap: 10px; max-width: 430px; margin-inline: auto; }
.room-screen__header { display: flex; flex-direction: column; gap: 4px; padding: 2px 0 3px; text-align: center; }
.room-screen__header h1 { font-size: 25px; }
.room-screen__note { color: var(--cb-text-2); font-size: 13px; }
.room-screen__cover { position: relative; width: 100%; aspect-ratio: 2.3; margin: 0; border-radius: 18px; overflow: hidden; border: 1px solid var(--cb-border-strong); background: var(--cb-surface); }
.room-screen__cover img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: 50% 43%; }
.room-screen__cover::after { position: absolute; inset: 42% 0 0; content: ""; background: linear-gradient(transparent, var(--cb-overlay)); }
.room-screen__cover figcaption { position: absolute; z-index: 1; bottom: 10px; left: 8px; right: 8px; height: 22px; text-align: center; font-size: clamp(14px, 4.1vw, 18px); line-height: 22px; font-weight: 800; text-shadow: 0 2px 8px var(--cb-bg); }
.room-screen__mode { display: flex; flex-direction: column; gap: 6px; }
.room-screen h2 { font-size: 15px; line-height: 20px; }
.room-screen__mode :deep(.mode-switch) { padding: 3px; border-radius: 13px; }
.room-screen__mode :deep(.mode-switch__option) { height: 44px; font-size: 13px; gap: 7px; }
.room-screen__mode :deep(.mode-switch__thumb) { top: 3px; bottom: 3px; left: 3px; width: calc(50% - 3px); border-radius: 10px; }
.room-screen__mode-note { min-height: 17px; font-size: 11px; line-height: 17px; color: var(--cb-text-2); text-align: center; }
.room-screen__guest-mode { padding: 10px 0; color: var(--cb-text-2); font-size: 14px; }
.room-screen__camera-card { display: flex; align-items: center; gap: 12px; padding: 7px 9px; border-radius: 16px; }
.room-screen__preview { position: relative; flex: 0 0 96px; width: 96px; height: 64px; overflow: hidden; border-radius: 11px; background: var(--cb-bg); }
.room-screen__video { width: 100%; height: 100%; object-fit: cover; transform: scaleX(-1); }
.room-screen__camera-icon { position: absolute; left: 34px; top: 18px; color: var(--cb-text-2); }
.room-screen__camera-copy { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.room-screen__camera-copy strong { font-size: 13px; line-height: 18px; }
.room-screen__camera-copy > span { padding-left: 16px; font-size: 12px; color: var(--cb-text-2); }
.room-screen__dot { display: inline-block; vertical-align: middle; width: 8px; height: 8px; margin-right: 7px; border-radius: 50%; background: var(--cb-text-3); }
.room-screen__dot--ready { background: var(--cb-lime); box-shadow: var(--cb-glow-sm); }
.room-screen__invite { display: flex; flex-direction: column; gap: 5px; padding: 10px; border-radius: 17px; }
.room-screen__invitation-header { display: flex; align-items: center; gap: 9px; }
.room-screen__invitation-header :deep(svg) { flex-shrink: 0; color: var(--cb-lime); }
.room-screen__link-row { display: flex; align-items: center; min-width: 0; height: 44px; margin-left: 32px; border: 1px solid var(--cb-border-strong); border-radius: 8px; background: var(--cb-surface-3); }
.room-screen__link { width: 100%; min-width: 0; height: 100%; padding: 4px 8px; border: 0; outline: none; background: transparent; color: var(--cb-text); font-size: 12px; text-overflow: ellipsis; }
.room-screen__copy-icon { display: flex; align-items: center; justify-content: center; flex: 0 0 44px; height: 44px; padding: 0; border: 0; border-radius: 7px; color: var(--cb-text-2); background: transparent; cursor: pointer; }
.room-screen__link-note, .room-screen__status { margin-left: 32px; font-size: 11px; line-height: 16px; color: var(--cb-text-2); }
.room-screen__participants { display: flex; flex-direction: column; gap: 7px; padding-top: 7px; margin-top: 2px; border-top: 1px solid var(--cb-border); }
.room-screen__participant { display: flex; align-items: center; min-height: 36px; gap: 10px; }
.room-screen__participant > div:last-child { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.room-screen__participant strong { font-size: 12px; font-weight: 600; }
.room-screen__participant strong > span { color: var(--cb-text-2); font-weight: 400; }
.room-screen__participant div > span { font-size: 11px; line-height: 15px; color: var(--cb-text-2); }
.room-screen__guest-avatar { flex: 0 0 36px; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; border: 1px dashed var(--cb-text-3); border-radius: 50%; color: var(--cb-text-2); }
.room-screen__actions { display: flex; flex-direction: column; gap: 8px; margin-top: 3px; }
.room-screen__actions :deep(.app-button--md) { height: 44px; border-radius: 12px; font-size: 14px; padding-inline: 12px; }
.room-screen__error { font-size: 12px; color: var(--cb-danger); line-height: 1.5; }
.room-screen__exit { align-self: center; min-height: 44px; border: 0; background: transparent; color: var(--cb-text-2); padding: 4px 12px; font-size: 12px; cursor: pointer; }
.room-screen__exit:disabled { opacity: 0.6; cursor: default; }
@media (max-width: 340px) { .room-screen { gap: 8px; padding-inline: 12px; } .room-screen__camera-card { gap: 9px; } .room-screen__preview { flex-basis: 86px; width: 86px; } .room-screen__header h1 { font-size: 23px; } }
</style>